import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { DashboardError } from '../../components/JobsAccount';
import { book } from '../../routers/books';
import {
  IResumeContactRequest,
  ResumeContactRequestStatus,
  useGetEmployerResumeContactRequestsQuery,
} from '../../services/serviceResume';

const statusLabels: Record<ResumeContactRequestStatus, string> = {
  0: 'Awaiting the candidate',
  1: 'Approved',
  2: 'Declined',
  3: 'Expired',
  4: 'Access revoked',
};

const statusColors: Record<ResumeContactRequestStatus, 'default' | 'success' | 'warning' | 'error'> = {
  0: 'warning',
  1: 'success',
  2: 'default',
  3: 'default',
  4: 'error',
};

const formatMoment = (value: string | null): string | null => {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : null;
};

const RequestRow = ({ request }: { request: IResumeContactRequest }) => {
  const sent = formatMoment(request.createdDate);
  const responded = formatMoment(request.respondedDate);
  const expires = formatMoment(request.expiresDate);

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      spacing={2}
      sx={{ p: 2.5 }}
    >
      <Box>
        <Typography
          component={Link}
          to={book.resumeDatabaseDetail(request.rguid)}
          fontWeight={800}
          color="inherit"
        >
          {request.candidateDisplayName}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {request.listingTitle || 'Listing no longer available'}
          {sent ? ` · Sent ${sent}` : ''}
          {request.status === 0 && expires ? ` · Expires ${expires}` : ''}
          {request.status !== 0 && responded ? ` · Answered ${responded}` : ''}
        </Typography>
        {request.status === 4 ? (
          <Typography color="error.main" variant="body2" sx={{ mt: 0.5 }}>
            This candidate withdrew access. Their contact details are no longer available to you.
          </Typography>
        ) : null}
        {request.needsRetry ? (
          <Typography color="warning.main" variant="body2" sx={{ mt: 0.5 }}>
            The notification email could not be sent. Open the candidate and send the request again.
            It will not use another introduction.
          </Typography>
        ) : null}
      </Box>
      <Chip
        label={statusLabels[request.status] || 'Unknown'}
        color={statusColors[request.status] || 'default'}
        sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, flexShrink: 0 }}
      />
    </Stack>
  );
};

// One page of the descending-ID feed. Each page owns its own query so adding a
// cursor never re-orders or re-runs the pages already on screen.
const RequestHistoryPage = ({
  beforeId,
  isFirst,
  isLast,
  onLoadOlder,
}: {
  beforeId?: number;
  isFirst: boolean;
  isLast: boolean;
  onLoadOlder: (nextId: number) => void;
}) => {
  const page = useGetEmployerResumeContactRequestsQuery(beforeId ? { beforeId } : {});
  const rows = page.data?.rows || [];
  const nextId = page.data?.nextId || null;

  return (
    <>
      <DashboardError error={page.error} fallback="We could not load your introduction requests." />
      {rows.map((request) => (
        <React.Fragment key={request.id}>
          <RequestRow request={request} />
          <Divider flexItem />
        </React.Fragment>
      ))}
      {isFirst && page.data && !rows.length ? (
        <Typography color="text.secondary" sx={{ p: 3 }}>
          You have not requested an introduction yet.
        </Typography>
      ) : null}
      {page.isFetching ? (
        <Stack alignItems="center" sx={{ p: 3 }}><CircularProgress size={24} /></Stack>
      ) : null}
      {isLast && nextId ? (
        <Box sx={{ p: 2.5, textAlign: 'center' }}>
          <Button variant="outlined" onClick={() => onLoadOlder(nextId)}>Load older requests</Button>
        </Box>
      ) : null}
    </>
  );
};

export const EmployerRequestHistory = () => {
  // Each entry is one loaded page's `beforeId`; the first page has none.
  const [cursors, setCursors] = useState<Array<number | undefined>>([undefined]);

  return (
    <>
      <Alert severity="info" sx={{ mb: 3 }}>
        Candidates choose whether to share their contact details. Approved candidates stay available
        here unless they later withdraw access.
      </Alert>
      <Paper variant="outlined" sx={{ borderRadius: 0 }}>
        <Stack>
          {cursors.map((cursor, index) => (
            <RequestHistoryPage
              key={cursor === undefined ? 'newest' : cursor}
              beforeId={cursor}
              isFirst={index === 0}
              isLast={index === cursors.length - 1}
              onLoadOlder={(nextId) => setCursors((current) => (
                current.indexOf(nextId) >= 0 ? current : current.concat([nextId])
              ))}
            />
          ))}
        </Stack>
      </Paper>
    </>
  );
};

export default EmployerRequestHistory;
